import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, BeakerIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Medicine } from '../../types/medicines';
import { medicinesApi } from '../../api/medicines';
import { diseasesApi } from '../../api/diseases';
import { Disease } from '../../types/diseases';
import toast from 'react-hot-toast';
import AddMedicineModal from './AddMedicineModal';

interface MedicinesListProps {
  onRefresh?: () => void;
}

const MedicinesList: React.FC<MedicinesListProps> = ({ onRefresh }) => {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Medicine | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [medicinesData, diseasesData] = await Promise.all([
        medicinesApi.getAll(),
        diseasesApi.getAll()
      ]);
      setMedicines(medicinesData);
      setDiseases(diseasesData);
    } catch (error) {
      console.error('Error loading medicines:', error);
      toast.error('Failed to load medicines');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedicine = async (medicineData: any) => {
    try {
      await medicinesApi.create(medicineData);
      setShowAddModal(false);
      await loadData();
      toast.success('Medicine added successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error adding medicine:', error);
      toast.error('Failed to add medicine');
    }
  };

  const handleEdit = (medicine: Medicine) => {
    setShowEditModal(medicine);
  };

  const handleUpdateMedicine = async (medicineId: number, data: any) => {
    try {
      await medicinesApi.update(medicineId, data);
      setShowEditModal(null);
      await loadData();
      toast.success('Medicine updated successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating medicine:', error);
      toast.error('Failed to update medicine');
    }
  };

  const handleDelete = async (medicine: Medicine) => {
    if (window.confirm(`Are you sure you want to delete "${medicine.name}"?`)) {
      try {
        await medicinesApi.delete(medicine.id);
        await loadData();
        toast.success('Medicine deleted successfully');
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error('Error deleting medicine:', error);
        toast.error('Failed to delete medicine');
      }
    }
  };

  const getDiseaseNames = (diseaseIds: number[]) => {
    return diseases
      .filter(disease => diseaseIds.includes(disease.id))
      .map(disease => disease.name)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (medicines.length === 0) {
    return (
      <div className="text-center py-12">
        <BeakerIcon className="mx-auto h-12 w-12 text-slate-400" />
        <h3 className="mt-2 text-sm font-semibold text-slate-900">No medicines</h3>
        <p className="mt-1 text-sm text-slate-500">
          Get started by creating a new medicine.
        </p>
        <div className="mt-6">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Medicine
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-slate-900">
          Medicines ({medicines.length})
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Medicine
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-0"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Manufacturer
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Active Ingredients
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Prescription Required
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Treats Diseases
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {medicines.map((medicine) => (
                      <tr key={medicine.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-slate-900 sm:pl-0">
                          {medicine.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {medicine.manufacturer || '-'}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500 max-w-xs truncate">
                          {medicine.activeIngredients || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {medicine.prescriptionRequired ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500 max-w-xs truncate">
                          {medicine.diseases?.map(d => d.name).join(', ') || '-'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(medicine)}
                              className="text-cyan-600 hover:text-indigo-900"
                              title="Edit medicine"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(medicine)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete medicine"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Medicine Modal */}
      {showAddModal && (
        <AddMedicineModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddMedicine}
          diseases={diseases}
        />
      )}

      {/* Edit Medicine Modal */}
      {showEditModal && (
        <AddMedicineModal
          isOpen={!!showEditModal}
          onClose={() => setShowEditModal(null)}
          onSubmit={(data) => handleUpdateMedicine(showEditModal!.id, data)}
          diseases={diseases}
          medicine={showEditModal}
        />
      )}
    </div>
  );
};

export default MedicinesList; 